import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Input, Button, Form, message, Tag, Alert } from 'antd';
import { makeGetCall, makePostCall, getUserDetails, hasPermission } from '../../utils/helper';
import FolderTree, { FolderTreeNode } from './components/FolderTree';
import { useUserCenters, useCenterPicker } from './components/CenterPicker';

type ExistingPlacement = {
  lb_id: number;
  lb_folder_id: number;
  lb_bookmarked_by: string;
  folder_name: string;
  lf_visibility: 'public' | 'private';
};

type ExistingPlacements = Record<string, ExistingPlacement[]>;

const { TextArea } = Input;

type LibraryFolder = {
  lf_id: number;
  lf_parent_id: number | null;
  lf_name: string;
  lf_visibility: 'private' | 'public';
  lf_owner_username: string | null;
  lf_center_code?: string | null;
};

type StudyRecord = {
  ps_study_uid?: string;
  pacs_order?: {
    pacs_ord_id?: number;
    po_acc_no?: string;
    po_modality?: string;
    po_body_part?: string;
    po_req_time?: string;
    patient?: {
      pat_name?: string;
      pat_age?: string | number;
      pat_sex?: string;
    };
  };
};

type Props = {
  visible: boolean;
  studies: StudyRecord[];
  onClose: () => void;
  onSaved?: () => void;
};

const studyToPayload = (s: StudyRecord) => ({
  study_uid: s.ps_study_uid,
  pacs_ord_id: s.pacs_order?.pacs_ord_id || null,
  accession_no: s.pacs_order?.po_acc_no || null,
  modality: s.pacs_order?.po_modality || null,
  body_part: s.pacs_order?.po_body_part || null,
  study_date: s.pacs_order?.po_req_time || null,
  patient_name: s.pacs_order?.patient?.pat_name || null,
  patient_age: s.pacs_order?.patient?.pat_age != null ? String(s.pacs_order.patient.pat_age) : null,
  patient_sex: s.pacs_order?.patient?.pat_sex || null
});

const toTreeNodes = (folders: LibraryFolder[]): FolderTreeNode[] =>
  folders.map(f => ({ id: f.lf_id, parentId: f.lf_parent_id, name: f.lf_name }));

const AddToLibraryDialog: React.FC<Props> = ({ visible, studies, onClose, onSaved }) => {
  const [folders, setFolders] = useState<LibraryFolder[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState<number | null>(null);
  const [remarks, setRemarks] = useState('');
  const [existingPlacements, setExistingPlacements] = useState<ExistingPlacements>({});

  const userCenters = useUserCenters();
  const { pickCenter, modal: centerPickerModal } = useCenterPicker(userCenters);
  const centerLabelByCode = useMemo(
    () => new Map(userCenters.map(c => [c.value, c.label])),
    [userCenters]
  );

  const usableStudies = useMemo(
    () => (studies || []).filter(s => !!s?.ps_study_uid),
    [studies]
  );

  const publicFolders = useMemo(
    () => folders.filter(f => f.lf_visibility === 'public'),
    [folders]
  );
  const privateFolders = useMemo(
    () => folders.filter(f => f.lf_visibility === 'private'),
    [folders]
  );
  const publicTreeNodes = useMemo<FolderTreeNode[]>(() => {
    const showCenter = userCenters.length > 1;
    return publicFolders.map(f => {
      const label = f.lf_center_code ? centerLabelByCode.get(f.lf_center_code) : null;
      const name = showCenter && f.lf_parent_id == null && label
        ? `${f.lf_name} (${label})`
        : f.lf_name;
      return { id: f.lf_id, parentId: f.lf_parent_id, name };
    });
  }, [publicFolders, userCenters, centerLabelByCode]);
  const privateTreeNodes = useMemo(() => toTreeNodes(privateFolders), [privateFolders]);

  const loadFolders = async () => {
    setLoading(true);
    try {
      const res = await makeGetCall('/library/folders');
      setFolders(res?.data?.data || []);
    } catch (err) {
      console.error('Failed to load folders', err);
      message.error('Failed to load folders');
      setFolders([]);
    } finally {
      setLoading(false);
    }
  };

  const loadExistingPlacements = async (uids: string[]) => {
    if (uids.length === 0) {
      setExistingPlacements({});
      return;
    }
    try {
      const res = await makeGetCall(
        `/library/bookmarks/lookup?study_uids=${encodeURIComponent(uids.join(','))}`
      );
      setExistingPlacements(res?.data?.data || {});
    } catch (err) {
      console.error('Failed to lookup existing bookmarks', err);
      setExistingPlacements({});
    }
  };

  useEffect(() => {
    if (visible) {
      setSelectedFolderId(null);
      setRemarks('');
      setExistingPlacements({});
      loadFolders();
      loadExistingPlacements(usableStudies.map(s => s.ps_study_uid!).filter(Boolean));
    }
  }, [visible]);

  const handleCreateFolder = async (
    parentId: number | null,
    name: string,
    visibility: 'public' | 'private'
  ) => {
    let center_code: string | null | undefined;
    if (visibility === 'public' && parentId == null) {
      try {
        center_code = await pickCenter();
      } catch {
        return; // user cancelled center selection — abort create
      }
    }

    try {
      const res = await makePostCall('/library/folders', { name, parent_id: parentId, visibility, center_code });
      const created: LibraryFolder = res?.data?.data;
      await loadFolders();
      if (created) setSelectedFolderId(created.lf_id);
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to create folder';
      message.error(msg);
      throw err;
    }
  };

  const handleSave = async () => {
    if (!selectedFolderId) {
      message.warning('Select a folder');
      return;
    }
    if (usableStudies.length === 0) {
      message.warning('No studies to bookmark');
      return;
    }
    setSaving(true);
    try {
      const res = await makePostCall('/library/bookmarks', {
        folder_id: selectedFolderId,
        remarks: remarks.trim() || null,
        studies: usableStudies.map(studyToPayload)
      });
      const created = res?.data?.count ?? 0;
      const skipped = res?.data?.skipped ?? 0;
      if (created > 0 && skipped > 0) {
        message.success(
          `Added ${created} ${created === 1 ? 'study' : 'studies'}; skipped ${skipped} already in this folder`
        );
      } else if (created > 0) {
        message.success(`Added ${created} ${created === 1 ? 'study' : 'studies'} to Library`);
      } else {
        message.info('All selected studies are already in this folder');
      }
      if (onSaved) onSaved();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to save bookmarks';
      message.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const canManagePublicFolders =
    getUserDetails()?.user_type === 'admin' || hasPermission('MANAGE_LIB_FOLDERS');

  const studyCount = usableStudies.length;
  const title = `Add ${studyCount} ${studyCount === 1 ? 'study' : 'studies'} to Library`;

  // Distinct folders this batch is already saved into (across all selected studies).
  const distinctExistingFolders = useMemo(() => {
    const seen = new Map<number, ExistingPlacement>();
    Object.values(existingPlacements).forEach(list => {
      list.forEach(p => { if (!seen.has(p.lb_folder_id)) seen.set(p.lb_folder_id, p); });
    });
    return Array.from(seen.values());
  }, [existingPlacements]);

  // Studies that are already in the currently-selected folder (will be skipped on save).
  const duplicateCountInSelected = useMemo(() => {
    if (!selectedFolderId) return 0;
    let n = 0;
    Object.values(existingPlacements).forEach(list => {
      if (list.some(p => p.lb_folder_id === selectedFolderId)) n += 1;
    });
    return n;
  }, [existingPlacements, selectedFolderId]);

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={onClose}
      width={640}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={saving}>Cancel</Button>,
        <Button
          key="save"
          type="primary"
          loading={saving}
          onClick={handleSave}
          disabled={!selectedFolderId || studyCount === 0}
        >
          Save to Folder
        </Button>
      ]}
      destroyOnClose
    >
      {distinctExistingFolders.length > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={
            studyCount === 1
              ? 'This study is already saved in:'
              : 'Some of these studies are already saved in:'
          }
          description={
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
              {distinctExistingFolders.map(p => (
                <Tag key={p.lb_folder_id} color={p.lf_visibility === 'public' ? 'blue' : 'default'}>
                  {p.folder_name}
                  <span style={{ marginLeft: 6, opacity: 0.65, fontSize: 11 }}>
                    {p.lf_visibility === 'public' ? 'Public' : 'Private'}
                  </span>
                </Tag>
              ))}
            </div>
          }
        />
      )}

      <div style={{
        border: '1px solid #E2E8F1',
        borderRadius: 6,
        padding: 8,
        minHeight: 220,
        maxHeight: 360,
        overflowY: 'auto',
        background: '#FAFBFD',
      }}>
        {/* Public folders are only a push target for users with "Push to Academics"
            (MANAGE_LIB_FOLDERS); enforced server-side too. Others use My Library. */}
        {canManagePublicFolders && (
          <>
            <FolderTree
              folders={publicTreeNodes}
              selectedId={selectedFolderId}
              onSelect={setSelectedFolderId}
              onCreate={(parentId, name) => handleCreateFolder(parentId, name, 'public')}
              loading={loading}
              canMutate={canManagePublicFolders}
              title="Public"
              emptyText="No public folders yet."
            />
            <div style={{ height: 14 }} />
          </>
        )}
        <FolderTree
          folders={privateTreeNodes}
          selectedId={selectedFolderId}
          onSelect={setSelectedFolderId}
          onCreate={(parentId, name) => handleCreateFolder(parentId, name, 'private')}
          loading={loading}
          title="My Library"
          emptyText="No folders yet. Click New folder to create one."
        />
      </div>

      {duplicateCountInSelected > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginTop: 12 }}
          message={
            duplicateCountInSelected === studyCount
              ? `All ${studyCount} ${studyCount === 1 ? 'study is' : 'studies are'} already in this folder — nothing will be added.`
              : `${duplicateCountInSelected} of ${studyCount} studies are already in this folder and will be skipped.`
          }
        />
      )}

      <Form layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item label="Remarks (optional, applies to all selected studies)">
          <TextArea
            rows={3}
            value={remarks}
            onChange={e => setRemarks(e.target.value)}
            placeholder="e.g. Classic Westermark sign — teaching case"
            maxLength={2000}
            showCount
          />
        </Form.Item>
      </Form>

      {centerPickerModal}
    </Modal>
  );
};

export default AddToLibraryDialog;
